/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { ICoreContext } from '@msdyn365-commerce/core';
import { CategoryHierarchy } from '@msdyn365-commerce/commerce-entities';
import { CategoryProductsResponse } from '../../../integrations/relewise/types/mapped';
import { ProductListMetadataBreadcrumb, ProductListMetadataVariant } from '../components/product-list-head-metadata';

export interface BuildPlpMetadataArgs {
    context: ICoreContext;
    activeProducts: CategoryProductsResponse | undefined;
    currentCategory: CategoryHierarchy | null;
    parentCategory: CategoryHierarchy | null;
    currentPage: number;
    isCategoryPage: boolean;
    pageTitle: string | undefined;
    homeLabel: string;
}

export interface PlpMetadataResult {
    pageSummary: {
        title?: string;
        description?: string;
        canonicalUrl?: string;
        sharingImageUrl?: string;
        redirectUrl?: string;
    };
    headMetadata: {
        pageUrl: string;
        pageName: string;
        websiteName: string;
        websiteUrl: string;
        description?: string;
        totalItems: number;
        currentPage: number;
        itemsPerPage: number;
        variants: ProductListMetadataVariant[];
        breadcrumbs: ProductListMetadataBreadcrumb[];
    };
}

const tryParseUrl = (raw: URL | string | undefined): URL | undefined => {
    if (!raw) {
        return undefined;
    }
    if (raw instanceof URL) {
        return raw;
    }
    try {
        return new URL(raw);
    } catch {
        return undefined;
    }
};

const toAbsolute = (origin: string, relativeOrAbsolute: string | undefined): string => {
    if (!relativeOrAbsolute) {
        return origin;
    }
    if (/^https?:\/\//i.test(relativeOrAbsolute)) {
        return relativeOrAbsolute;
    }
    return `${origin}${relativeOrAbsolute.startsWith('/') ? '' : '/'}${relativeOrAbsolute}`;
};

export const buildPlpMetadata = ({
    context,
    activeProducts,
    currentCategory,
    parentCategory,
    currentPage,
    isCategoryPage,
    pageTitle,
    homeLabel
}: BuildPlpMetadataArgs): PlpMetadataResult => {
    const request = context.request;
    const canonicalDomain = request.canonicalDomain;
    const requestUrl = tryParseUrl(request.url?.requestUrl);

    const origin = canonicalDomain ? `https://${canonicalDomain}` : requestUrl ? requestUrl.origin : '';

    const currentPath = requestUrl ? requestUrl.pathname : '';
    const baseCanonical = origin && currentPath ? `${origin}${currentPath}` : '';
    let canonicalUrl: string | undefined;
    if (baseCanonical) {
        canonicalUrl = currentPage > 1 ? `${baseCanonical}?page=${currentPage}` : baseCanonical;
    }

    const websiteName = canonicalDomain || '';
    const websiteUrl = origin || '';

    const seoTitle = activeProducts?.seoTitle;
    const seoText = activeProducts?.seoText;

    const summaryTitle = seoTitle || currentCategory?.Name || pageTitle;
    const summaryDescription = seoText || undefined;
    const sharingImageUrl = currentCategory?.OfflineImage || undefined;

    const variants: ProductListMetadataVariant[] = (activeProducts?.productList ?? [])
        .map(entry => entry.variant)
        .filter((variant): variant is NonNullable<typeof variant> & { finalPrice: number; priceCurrency: string } => {
            if (!variant) {
                return false;
            }
            return (
                Boolean(variant.displayName && variant.productItemId) &&
                typeof variant.finalPrice === 'number' &&
                typeof variant.priceCurrency === 'string' &&
                variant.priceCurrency.length > 0
            );
        })
        .map(variant => {
            const url = toAbsolute(origin, variant.productPageUrl);
            const groupUrl = toAbsolute(origin, variant.productPageUrl.split('?')[0]);
            return {
                url,
                name: variant.displayName,
                ...(variant.concatenatedId ? { sku: variant.concatenatedId } : {}),
                ...(variant.imgUrl ? { image: variant.imgUrl } : {}),
                ...(variant.brand ? { brand: variant.brand } : {}),
                ...(variant.color?.name ? { color: variant.color.name } : {}),
                productGroupUrl: groupUrl,
                price: variant.finalPrice,
                priceCurrency: variant.priceCurrency,
                isInStock: variant.isInStock ?? true,
                ...(variant.suggestedGender ? { suggestedGender: variant.suggestedGender } : {})
            };
        });

    const breadcrumbs: ProductListMetadataBreadcrumb[] = [];
    if (isCategoryPage && origin) {
        breadcrumbs.push({ name: homeLabel, url: `${origin}/` });

        if (parentCategory?.Name && parentCategory.Url) {
            breadcrumbs.push({ name: parentCategory.Name, url: toAbsolute(origin, parentCategory.Url) });
        }

        if (currentCategory?.Name && baseCanonical) {
            breadcrumbs.push({ name: currentCategory.Name, url: baseCanonical });
        }
    }

    const rawRedirectUrl = activeProducts?.redirects?.[0]?.url;
    const redirectUrl =
        rawRedirectUrl && (/^https?:\/\//i.test(rawRedirectUrl) || rawRedirectUrl.startsWith('/')) ? rawRedirectUrl : undefined;

    return {
        pageSummary: {
            title: summaryTitle,
            description: summaryDescription,
            canonicalUrl,
            sharingImageUrl,
            redirectUrl
        },
        headMetadata: {
            pageUrl: canonicalUrl ?? baseCanonical ?? '',
            pageName: summaryTitle ?? '',
            websiteName,
            websiteUrl,
            description: summaryDescription,
            totalItems: activeProducts?.totalItems ?? 0,
            currentPage,
            itemsPerPage: activeProducts?.itemsPerPage ?? variants.length,
            variants,
            breadcrumbs
        }
    };
};
